import { PrDTO } from "../../api/admin/types";

// "involves" is a filter field only, not a column
export type ColId =
  | "ci"
  | "repo"
  | "number"
  | "author"
  | "reviewer"
  | "title"
  | "status"
  | "age"
  | "draft"
  | "involves";

export const COLUMNS: { id: ColId; name: string; num?: boolean }[] = [
  { id: "ci", name: "CI" },
  { id: "repo", name: "Repo" },
  { id: "number", name: "#", num: true },
  { id: "author", name: "Author" },
  { id: "reviewer", name: "Reviewer" },
  { id: "title", name: "Title" },
  { id: "status", name: "Status" },
  { id: "age", name: "Age", num: true },
  { id: "draft", name: "Draft" },
];

// "many" = how a chip names several picked values, e.g. "3 statuses"
export const FIELDS: { col: ColId; name: string; many: string }[] = [
  { col: "ci", name: "CI", many: "CI states" },
  { col: "repo", name: "Repo", many: "repos" },
  { col: "author", name: "Author", many: "authors" },
  { col: "reviewer", name: "Reviewer", many: "reviewers" },
  { col: "status", name: "Status", many: "statuses" },
  { col: "draft", name: "Draft", many: "draft states" },
  { col: "involves", name: "Involves", many: "people" },
];

export type Section = "review" | "rereview" | "mine" | "author" | "other";
export const SECTIONS: [Section, string][] = [
  ["review", "Your review"],
  ["rereview", "Re-review"],
  ["mine", "Your PRs"],
  ["author", "Author’s turn"],
  ["other", "Everyone else"],
];

export type Group = "sect" | "none" | ColId;
export const GROUPS: [Group, string][] = [
  ["sect", "Whose move"],
  ["repo", "Repo"],
  ["author", "Author"],
  ["status", "Status"],
  ["ci", "CI"],
  ["draft", "Draft"],
  ["none", "No grouping"],
];

export type Tone = "ok" | "bad" | "wait" | "none";
// status icons, Linear style: empty = waiting on review, dashed = author's turn,
// half-filled = your move, check = approved, x = changes requested
export type StatusIcon = "neutral" | "author" | "you" | "ok" | "bad";

// CI rollup state -> (dot color, filter word, sort rank)
const CI: Record<string, [Tone, string, number]> = {
  SUCCESS: ["ok", "passing", 2],
  FAILURE: ["bad", "failing", 0],
  ERROR: ["bad", "error", 0],
  PENDING: ["wait", "running", 1],
  EXPECTED: ["wait", "queued", 1],
};
// reviewDecision -> (icon, label, sort rank)
const REVIEW: Record<string, [StatusIcon, string, number]> = {
  CHANGES_REQUESTED: ["bad", "changes", 0],
  APPROVED: ["ok", "approved", 2],
  REVIEW_REQUIRED: ["neutral", "awaiting", 1],
};
const HIDDEN_REVIEWERS = ["greptile-apps"];

export type Row = {
  i: number;
  url: string;
  number: number;
  repo: string;
  author: string;
  reviewers: string[];
  // author, requested reviewers, reviewers, commenters, assignees
  involved: string[];
  title: string;
  ci: { tone: Tone; word: string; rank: number };
  status: { icon: StatusIcon; label: string; rank: number };
  ageDays: number;
  draft: boolean;
  sect: Section;
  // PRs sharing a ticket id are shown as one joined block
  ticket?: string;
  gpos?: "start" | "mid" | "end";
};

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const daysOld = (iso: string, now: number) => (now - Date.parse(iso)) / 86400000;

export function ago(days: number): string {
  const s = days * 86400;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function sectionOf(p: PrDTO, me: string): Section {
  if (same(p.author, me)) return "mine";
  // requested wins: re-requested = back to you
  if (p.reviewRequests.some((r) => same(r, me))) return "review";
  const mine = p.reviews.filter((r) => same(r.login, me)).map((r) => r.submittedAt);
  if (!mine.length) return "other";
  // you reviewed it: your turn again only if the author pushed after your review
  const last = mine.sort().slice(-1)[0];
  return p.lastCommitAt && p.lastCommitAt > last ? "rereview" : "author";
}

function statusOf(p: PrDTO, sect: Section): Row["status"] {
  if (sect === "review") return { icon: "you", label: "your review", rank: 0 };
  if (sect === "rereview") return { icon: "you", label: "re-review", rank: 0 };
  if (sect === "author") return { icon: "author", label: "author’s turn", rank: 1 };
  const [icon, label, rank] = REVIEW[p.reviewDecision || ""] || REVIEW.REVIEW_REQUIRED;
  return { icon, label, rank };
}

// your PRs: failing CI first, then changes requested, then approved
function priority(r: Row): number {
  if (r.ci.tone === "bad") return 0;
  if (r.status.icon === "bad") return 1;
  if (r.status.icon === "ok") return 2;
  return 3;
}

export const ticketOf = (title: string) => {
  const m = /cov-(\d+)/i.exec(title);
  return m ? `COV-${m[1]}` : undefined;
};

/** One list in the default order: your PRs, then ones you're reviewing, then everyone else's. */
export function buildRows(prs: PrDTO[], me: string, now = Date.now()): Row[] {
  const rows: Row[] = prs.map((p) => {
    const sect = sectionOf(p, me);
    const [tone, word, rank] = CI[p.ci || ""] || ["none", "none", 3];
    const who = [...p.reviewRequests, ...p.reviews.map((r) => r.login)];
    return {
      i: 0,
      url: p.url,
      number: p.number,
      repo: p.repo,
      author: p.author,
      // the author's own replies don't count as reviews
      reviewers: Array.from(new Set(who)).filter(
        (w) => w && !same(w, p.author) && !HIDDEN_REVIEWERS.includes(w)
      ),
      involved: Array.from(new Set([p.author, ...who, ...(p.involved || [])])).filter(Boolean),
      title: p.title,
      ci: { tone, word, rank },
      status: statusOf(p, sect),
      ageDays: daysOld(p.updatedAt, now),
      draft: p.isDraft,
      sect,
    };
  });
  const yourTurn = (r: Row) => (r.sect === "review" || r.sect === "rereview" ? 0 : 1);
  const mine = rows.filter((r) => r.sect === "mine").sort((a, b) => priority(a) - priority(b));
  const reviewing = rows
    .filter((r) => ["review", "rereview", "author"].includes(r.sect))
    .sort((a, b) => yourTurn(a) - yourTurn(b) || a.ageDays - b.ageDays);
  const other = rows.filter((r) => r.sect === "other").sort((a, b) => a.ageDays - b.ageDays);
  return joinTickets([...mine, ...reviewing, ...other]).map((r, i) => ({ ...r, i }));
}

/** PRs sharing a ticket id sit in adjacent rows, in first-seen order. */
export function joinTickets(rows: Row[]): Row[] {
  const counts = new Map<string, number>();
  rows.forEach((r) => {
    const t = ticketOf(r.title);
    if (t) counts.set(t, (counts.get(t) || 0) + 1);
  });
  const out: Row[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    const t = ticketOf(r.title);
    if (!t || (counts.get(t) || 0) < 2) {
      out.push(r);
      continue;
    }
    if (seen.has(t)) continue;
    seen.add(t);
    const members = rows.filter((q) => ticketOf(q.title) === t);
    members.forEach((q, i) =>
      out.push({
        ...q,
        ticket: t,
        gpos: i === 0 ? "start" : i === members.length - 1 ? "end" : "mid",
      })
    );
  }
  return out;
}

/** The values a filter or grouping sees in a column; Reviewer can hold several. */
export function valuesOf(r: Row, col: ColId): string[] {
  switch (col) {
    case "ci":
      return [r.ci.word];
    case "repo":
      return [r.repo];
    case "author":
      return [r.author];
    case "reviewer":
      return r.reviewers;
    case "involves":
      return r.involved;
    case "status":
      return [r.status.label];
    case "draft":
      return [r.draft ? "draft" : "ready"];
    case "title":
      return [r.title];
    case "number":
      return [String(r.number)];
    case "age":
      return [ago(r.ageDays)];
  }
}

export function sortKey(r: Row, col: ColId): number | string {
  switch (col) {
    case "ci":
      return r.ci.rank;
    case "number":
      return r.number;
    case "status":
      return r.status.rank;
    case "age":
      return r.ageDays;
    case "draft":
      return r.draft ? 1 : 0;
    default:
      return valuesOf(r, col).join(" ").toLowerCase();
  }
}

export const has = (r: Row, col: ColId, v: string) => valuesOf(r, col).some((x) => same(x, v));

export const distinctValues = (rows: Row[], col: ColId) =>
  Array.from(new Set(rows.flatMap((r) => valuesOf(r, col))))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

export type Filter = { col: ColId; op: "is" | "not"; values: string[] };
export type Match = "all" | "any";

// op "not" flips a filter; match "any" ORs the filters together
export const filterTest = (f: Filter) => (r: Row) => {
  const hit = f.values.some((v) => has(r, f.col, v));
  return f.op === "not" ? !hit : hit;
};

/** skip = leave one filter out, so a menu can count what picking a value would show. */
export function passes(
  r: Row,
  filters: Filter[],
  match: Match,
  title: string,
  skip?: Filter
): boolean {
  const tests = filters.filter((f) => f !== skip).map(filterTest);
  const ok =
    !tests.length || (match === "any" ? tests.some((t) => t(r)) : tests.every((t) => t(r)));
  return ok && r.title.toLowerCase().includes(title.trim().toLowerCase());
}

export const groupOf = (r: Row, group: Group) =>
  group === "sect" ? r.sect : group === "none" ? "" : valuesOf(r, group)[0] || "";

/** Rows grouped and ordered; a ticket's joined rows move together when sorted. */
export function layout(
  rows: Row[],
  group: Group,
  sortCol: ColId | undefined,
  sortDir: number
): { key: string; rows: Row[] }[] {
  const cmp = (x: number | string, y: number | string) => (x < y ? -sortDir : x > y ? sortDir : 0);
  const keys =
    group === "sect"
      ? SECTIONS.map((s) => s[0] as string)
      : group === "none"
        ? [""]
        : Array.from(new Set(rows.map((r) => groupOf(r, group)))).sort((a, b) =>
            a.localeCompare(b)
          );
  return keys.map((key) => {
    const blocks = new Map<string, Row[]>();
    rows
      .filter((r) => groupOf(r, group) === key)
      .forEach((r) => {
        const k = r.ticket || r.url;
        blocks.set(k, [...(blocks.get(k) || []), r]);
      });
    let bs = Array.from(blocks.values());
    if (sortCol === undefined) bs.sort((a, b) => a[0].i - b[0].i);
    else {
      const col = sortCol;
      bs = bs
        .map((b) => ({ b, k: b.map((r) => sortKey(r, col)).sort(cmp)[0] }))
        .sort((a, b) => cmp(a.k, b.k))
        .map((x) => x.b);
    }
    return { key, rows: bs.flat() };
  });
}
